import React, { useEffect, useRef, useState } from 'react';
import { FiChevronLeft, FiPlus, FiTrash2, FiUploadCloud, FiX, FiPaperclip } from 'react-icons/fi';
import type { CancellationPolicy, OfferItem, OfferStatus } from '../types';
import { toast } from '../../../../utils/toast';
import {
  buildCreateOfferPayload,
  buildUpdateOfferPayload,
  findConflictingActiveOffer,
  useCreateOffer,
  useOffersList,
  useSubmitOffer,
  useUpdateOffer,
} from '../../../../api/offers';
import { isStripeAccountReady, useStripeAccountStatus } from '../../../../api/stripe';
import { MIN_OFFER_PAYMENT_AMOUNT } from './paymentLinks';
import { parseProposedPrice } from './changeRequests';
import {
  MILESTONE_TOTAL_MESSAGE,
  MIN_MILESTONE_AMOUNT_MESSAGE,
  MIN_OFFER_AMOUNT_MESSAGE,
  reconcileMilestoneAmounts,
  toCalendarDate,
} from './offerPublishChecks';
import { StripeOnboardingPromptModal } from './StripeOnboardingPromptModal';
import { useOpenConversation } from '../../../../api/conversations';
import { uploadFilesToCloudinary } from '../../../../utils/cloudinary';
import {
  OFFER_ATTACHMENT_ACCEPT_ATTRIBUTE,
  UPLOAD_MAX_MEDIA_FILES,
  validateOfferAttachmentFile,
} from '../../../../utils/uploadValidation';
import { FormLabel } from '../../../ui/FormLabel';
import { InfoTip, Tooltip } from '../../../ui/Tooltip';
import { cancellationPolicyTerms, offerFormTips } from '../../../../onboarding/copy';
import { TOUR, tourTarget } from '../../../../onboarding/targets';
import { TakeTourButton } from '../../../../onboarding/TakeTourButton';
import { Select } from '../../../ui/Select';
import { TimezoneSelect, resolveDefaultTimezone } from '../../../ui/TimezoneSelect';
import { OfferFeeSummary } from './OfferFeeSummary';
import {
  formatDateTimeLong,
  splitDateTimeInZone,
  todayInZone,
  zonedDateTimeIso,
} from '../../../../utils/eventDate';

export interface CreateOfferDefaults {
  recipientEmail?: string;
  recipientName?: string;
  recipientUserId?: number;
  conversationId?: string;
  title?: string;
  description?: string;
  applicationUuid?: string;
  gigUuid?: string;
  predecessorOfferUuid?: string;
}

export interface OfferSubmitResult {
  offerId: string;
  conversationId?: string | null;
  status: OfferStatus;
}

interface CreateOfferViewProps {
  onBack: () => void;
  onSubmitSuccess: (result?: OfferSubmitResult) => void;
  initialOffer?: OfferItem;
  isEdit?: boolean;
  defaults?: CreateOfferDefaults;
}

type FieldErrors = Partial<
  Record<
    | 'title'
    | 'description'
    | 'deliverables'
    | 'price'
    | 'cancellationPolicy'
    | 'startDate'
    | 'deliveryDate'
    | 'expirationDate'
    | 'expirationTime'
    | 'expirationTimezone'
    | 'recipientEmail'
    | 'milestones',
    string
  >
>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function todayIsoDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const fieldClass =
  'w-full rounded-2xl border border-white/15 bg-[#121214] px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500/40 focus:border-blue-500/40 transition';
const fieldErrorClass = 'border-red-500/60 focus:ring-red-500/30 focus:border-red-500/50';
const dateClass = `${fieldClass} scheme-dark [color-scheme:dark]`;
const numberClass = `${fieldClass} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`;
const nestedFieldClass =
  'w-full min-w-0 rounded-xl border border-white/15 bg-[#1C1C1E] px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500/40 focus:border-blue-500/40 transition';
const nestedDateClass = `${nestedFieldClass} scheme-dark [color-scheme:dark]`;
const nestedNumberClass = `${nestedFieldClass} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`;

const OFFER_CURRENCY = 'USD';
const OFFER_CURRENCY_LABEL = 'USD ($)';

export type OfferPaymentType = 'milestone' | 'single';

const PAYMENT_TYPE_OPTIONS: { value: OfferPaymentType; label: string }[] = [
  { value: 'single', label: 'Single payment' },
  { value: 'milestone', label: 'Milestone based' },
];

const CANCELLATION_OPTIONS: { value: CancellationPolicy; label: string }[] = (
  ['flexible', 'moderate', 'strict'] as const
).map((value) => ({ value, label: cancellationPolicyTerms[value].label }));

const cancellationTermsTip = (
  <div className="space-y-2">
    <p className="font-semibold text-white">Refund terms</p>
    {CANCELLATION_OPTIONS.map(({ value, label }) => (
      <p key={value}>
        <span className="font-semibold text-white">{label}:</span>{' '}
        {cancellationPolicyTerms[value].terms}
      </p>
    ))}
  </div>
);

const REVISION_OPTIONS = [
  { value: 0, label: '0 Revisions' },
  { value: 1, label: '1 Revision' },
  { value: 2, label: '2 Revisions' },
  { value: 3, label: '3 Revisions' },
  { value: 99, label: 'Unlimited' },
];

const toDateInputValue = (value?: string): string => toCalendarDate(value);

type OfferAttachment = { name: string; url: string; file_size?: number };
type OfferAttachmentWithSize = { name: string; url: string; file_size: number };

async function fetchRemoteFileSize(url: string): Promise<number | undefined> {
  try {
    const head = await fetch(url, { method: 'HEAD' });
    const fromHead = Number(head.headers.get('content-length'));
    if (Number.isFinite(fromHead) && fromHead >= 0) return fromHead;
  } catch {
    // fall through
  }
  try {
    const ranged = await fetch(url, { method: 'GET', headers: { Range: 'bytes=0-0' } });
    const contentRange = ranged.headers.get('content-range');
    const match = contentRange?.match(/\/(\d+)\s*$/);
    if (match) {
      const total = Number(match[1]);
      if (Number.isFinite(total) && total >= 0) return total;
    }
    const fromGet = Number(ranged.headers.get('content-length'));
    if (Number.isFinite(fromGet) && fromGet >= 0) return fromGet;
  } catch {
    // ignore
  }
  return undefined;
}

async function ensureAttachmentFileSizes(
  attachments: OfferAttachment[],
): Promise<OfferAttachmentWithSize[]> {
  const resolved: OfferAttachment[] = await Promise.all(
    attachments.map(async (att) => {
      if (att.file_size != null && Number.isFinite(att.file_size) && att.file_size >= 0) {
        return { name: att.name, url: att.url, file_size: att.file_size };
      }
      const size = await fetchRemoteFileSize(att.url);
      if (size == null) return { name: att.name, url: att.url };
      return { name: att.name, url: att.url, file_size: size };
    }),
  );

  const missing = resolved.find((a) => a.file_size == null || !Number.isFinite(a.file_size));
  if (missing) {
    toast.error(
      `Could not determine file size for "${missing.name}". Remove it and re-attach the file.`,
    );
    throw new Error('Missing attachment file_size');
  }

  return resolved.map((a) => ({
    name: a.name,
    url: a.url,
    file_size: a.file_size as number,
  }));
}

function initialExpirationParts(
  offer?: OfferItem,
  carryOver = true,
): {
  date: string;
  time: string;
  timezone: string;
} {
  const timezone = resolveDefaultTimezone(offer?.timezone);
  if (!offer?.expirationDate || !carryOver) {
    return { date: '', time: '', timezone };
  }
  try {
    const parts = splitDateTimeInZone(offer.expirationDate, timezone);
    return { date: parts.date, time: parts.time, timezone };
  } catch {
    return { date: toDateInputValue(offer.expirationDate), time: '', timezone };
  }
}

function initialPrice(offer?: OfferItem): number | '' {
  if (!offer) return '';
  if (offer.status === 'changes_requested') {
    const proposed = parseProposedPrice(offer);
    if (proposed != null) return proposed;
  }
  return offer.price ?? '';
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="mt-1.5 text-[12px] text-red-400">{message}</p>;
}

function currencySymbol(code: string): string {
  if (code === 'EUR') return '€';
  if (code === 'GBP') return '£';
  return '$';
}

export const CreateOfferView: React.FC<CreateOfferViewProps> = ({
  onBack,
  onSubmitSuccess,
  initialOffer,
  isEdit = false,
  defaults,
}) => {
  const createOffer = useCreateOffer();
  const updateOffer = useUpdateOffer();
  const submitOffer = useSubmitOffer();
  const openConversation = useOpenConversation();
  const { refetch: refetchMyOffers } = useOffersList({ perspective: 'created', page_size: 100 });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState(initialOffer?.title || defaults?.title || '');
  const [description, setDescription] = useState(
    initialOffer?.description || defaults?.description || '',
  );
  const [deliverablesText, setDeliverablesText] = useState(
    Array.isArray(initialOffer?.deliverables)
      ? initialOffer.deliverables.join('\n')
      : typeof initialOffer?.deliverables === 'string'
        ? initialOffer.deliverables
        : '',
  );
  const currency = OFFER_CURRENCY;
  const [price, setPrice] = useState<number | ''>(initialPrice(initialOffer));
  const [paymentType, setPaymentType] = useState<OfferPaymentType>(
    initialOffer?.paymentType === 'milestone' ? 'milestone' : 'single',
  );
  const [cancellationPolicy, setCancellationPolicy] = useState<CancellationPolicy | ''>(
    initialOffer?.cancellationPolicy || '',
  );

  const [milestones, setMilestones] = useState<
    { id: string; title: string; percentage: number; amount: number; dueDate: string }[]
  >(
    initialOffer?.milestones && initialOffer.milestones.length > 0
      ? initialOffer.milestones.map((m) => {
          const pct =
            m.percentage != null && Number.isFinite(m.percentage)
              ? m.percentage
              : (m.amount / (initialOffer.price || 1)) * 100;
          const amt =
            m.amount != null && Number.isFinite(m.amount)
              ? m.amount
              : ((initialOffer.price || 0) * (pct || 0)) / 100;
          return {
            id: m.id,
            title: m.title,
            percentage: Math.round(pct * 100) / 100,
            amount: Math.round(amt * 100) / 100,
            dueDate: toDateInputValue(m.dueDate),
          };
        })
      : [],
  );

  const [startDate, setStartDate] = useState(toDateInputValue(initialOffer?.startDate));
  const [deliveryDate, setDeliveryDate] = useState(toDateInputValue(initialOffer?.deliveryDate));
  const [revisionsIncluded, setRevisionsIncluded] = useState(initialOffer?.revisionsIncluded ?? 0);
  // A new offer started from an old one needs a fresh expiry; the old one is usually past.
  const [initialExpiration] = useState(() => initialExpirationParts(initialOffer, isEdit));
  const [expirationDate, setExpirationDate] = useState(initialExpiration.date);
  const [expirationTime, setExpirationTime] = useState(initialExpiration.time);
  const [expirationTimezone, setExpirationTimezone] = useState(initialExpiration.timezone);
  const [internalNotes, setInternalNotes] = useState(initialOffer?.internalNotes || '');
  const [recipientEmail, setRecipientEmail] = useState(
    initialOffer?.recipientEmail || defaults?.recipientEmail || '',
  );
  const [recipientName, setRecipientName] = useState(
    initialOffer?.recipientName || defaults?.recipientName || '',
  );
  const [recipientUserId, setRecipientUserId] = useState<number | null>(
    initialOffer?.recipientUserId ?? defaults?.recipientUserId ?? null,
  );
  const [conversationId] = useState<string | null>(
    initialOffer?.conversationId ?? defaults?.conversationId ?? null,
  );
  const [gigUuid] = useState<string | null>(initialOffer?.gigUuid ?? defaults?.gigUuid ?? null);
  const [applicationUuid] = useState<string | null>(defaults?.applicationUuid ?? null);
  const predecessorOfferUuid =
    defaults?.predecessorOfferUuid || (!isEdit ? initialOffer?.id : null) || null;

  const [existingAttachments, setExistingAttachments] = useState<OfferAttachment[]>(
    initialOffer?.attachments ?? [],
  );
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [showStripePrompt, setShowStripePrompt] = useState(false);

  const isDirectOffer = !gigUuid;
  const { data: stripeAccount, isLoading: stripeLoading } = useStripeAccountStatus();
  const [openedAt] = useState(() => Date.now());
  const expiryAlreadyPast =
    !!expirationDate &&
    !!expirationTime &&
    !!expirationTimezone &&
    Date.parse(zonedDateTimeIso(expirationDate, expirationTime, expirationTimezone)) <= openedAt;
  const sendsToChat = !isDirectOffer && (Boolean(conversationId) || recipientUserId != null);
  const recipientLocked =
    !isDirectOffer &&
    (recipientUserId != null || Boolean(recipientEmail) || Boolean(defaults?.applicationUuid));
  const numericPrice = typeof price === 'number' ? price : 0;
  const totalPercentage = milestones.reduce((sum, m) => sum + (m.percentage || 0), 0);
  const totalMilestoneAmount = milestones.reduce((sum, m) => sum + (m.amount || 0), 0);
  const attachmentCount = existingAttachments.length + pendingFiles.length;
  const attachmentsAtLimit = attachmentCount >= UPLOAD_MAX_MEDIA_FILES;
  const isSaving =
    submitting ||
    isUploading ||
    createOffer.isPending ||
    updateOffer.isPending ||
    submitOffer.isPending ||
    openConversation.isPending;

  const formTitle = isEdit
    ? initialOffer?.status === 'changes_requested'
      ? 'Send Revised Offer'
      : 'Edit Offer'
    : predecessorOfferUuid
      ? 'Send New Offer'
      : 'Create Offer';
  const defaultSendLabel = sendsToChat ? 'Send Offer to Chat' : 'Send Offer';
  const sendLabel = isEdit
    ? initialOffer?.status === 'changes_requested'
      ? 'Send Revised Offer'
      : defaultSendLabel
    : predecessorOfferUuid
      ? 'Send New Offer'
      : defaultSendLabel;

  const clearError = (key: keyof FieldErrors) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const scrollToFirstError = (errors: FieldErrors) => {
    const order: (keyof FieldErrors)[] = [
      'title',
      'description',
      'deliverables',
      'price',
      'cancellationPolicy',
      'milestones',
      'startDate',
      'deliveryDate',
      'expirationDate',
      'expirationTime',
      'expirationTimezone',
      'recipientEmail',
    ];
    const first = order.find((k) => errors[k]);
    if (!first) return;
    const el = document.querySelector(`[data-field="${first}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleAddMilestone = () => {
    const newId = 'm_' + Math.random().toString(36).substring(2, 7);
    setMilestones([...milestones, { id: newId, title: '', percentage: 0, amount: 0, dueDate: '' }]);
    clearError('milestones');
  };

  const handleRemoveMilestone = (id: string) => {
    setMilestones(milestones.filter((m) => m.id !== id));
    clearError('milestones');
  };

  const roundMoney = (n: number) => Math.round(n * 100) / 100;
  const roundPct = (n: number) => Math.round(n * 100) / 100;

  const handleMilestoneChange = (
    id: string,
    field: 'title' | 'percentage' | 'amount' | 'dueDate',
    value: string | number,
  ) => {
    setMilestones(
      milestones.map((m) => {
        if (m.id !== id) return m;
        if (field === 'percentage') {
          const raw = typeof value === 'number' ? value : Number(value);
          if (!Number.isFinite(raw)) return { ...m, percentage: 0, amount: 0 };
          const clamped = Math.min(100, Math.max(0, raw));
          const amount = numericPrice > 0 ? roundMoney((numericPrice * clamped) / 100) : m.amount;
          return { ...m, percentage: clamped, amount };
        }
        if (field === 'amount') {
          const raw = typeof value === 'number' ? value : Number(value);
          if (!Number.isFinite(raw)) return { ...m, amount: 0, percentage: 0 };
          const clamped =
            numericPrice > 0 ? Math.min(numericPrice, Math.max(0, raw)) : Math.max(0, raw);
          const percentage =
            numericPrice > 0 ? roundPct((clamped / numericPrice) * 100) : m.percentage;
          return { ...m, amount: roundMoney(clamped), percentage };
        }
        return { ...m, [field]: value };
      }),
    );
    clearError('milestones');
  };

  // Keep milestone amounts in sync when the offer price changes.
  useEffect(() => {
    if (numericPrice <= 0) return;
    setMilestones((prev) => {
      let changed = false;
      const next = prev.map((m) => {
        // Amount entered before price: derive % from amount
        if ((m.percentage || 0) === 0 && (m.amount || 0) > 0) {
          const amount = roundMoney(Math.min(m.amount, numericPrice));
          const percentage = roundPct((amount / numericPrice) * 100);
          if (percentage !== m.percentage || amount !== m.amount) {
            changed = true;
            return { ...m, percentage, amount };
          }
          return m;
        }
        const amount = roundMoney((numericPrice * (m.percentage || 0)) / 100);
        if (amount !== m.amount) {
          changed = true;
          return { ...m, amount };
        }
        return m;
      });
      return changed ? next : prev;
    });
  }, [numericPrice]);

  const handleFilesSelected = (files: FileList | null) => {
    if (!files?.length) return;

    const currentCount = existingAttachments.length + pendingFiles.length;
    const remaining = UPLOAD_MAX_MEDIA_FILES - currentCount;
    if (remaining <= 0) {
      toast.error(`You can attach up to ${UPLOAD_MAX_MEDIA_FILES} files.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const accepted: File[] = [];
    Array.from(files).forEach((file) => {
      const check = validateOfferAttachmentFile(file);
      if (!check.valid) {
        toast.error(check.error || `Could not add ${file.name}`);
        return;
      }
      accepted.push(file);
    });

    if (accepted.length > remaining) {
      toast.error(
        `Only the first ${remaining} file${remaining === 1 ? '' : 's'} added — ${UPLOAD_MAX_MEDIA_FILES} file limit reached.`,
      );
    }

    const toAdd = accepted.slice(0, remaining);
    if (toAdd.length) setPendingFiles((prev) => [...prev, ...toAdd]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const resolveAttachments = async (): Promise<
    { name: string; url: string; file_size: number }[]
  > => {
    let merged: OfferAttachment[] = existingAttachments;

    if (pendingFiles.length > 0) {
      setIsUploading(true);
      try {
        const results = await uploadFilesToCloudinary(pendingFiles, {
          folder: 'offer_attachments',
          uploadPreset: 'service_preset',
          resourceType: 'auto',
        });
        const uploaded = results.map((r, i) => ({
          name: pendingFiles[i]?.name || `Attachment ${i + 1}`,
          url: r.secure_url,
          file_size: r.bytes ?? pendingFiles[i]?.size,
        }));
        merged = [...existingAttachments, ...uploaded];
        setExistingAttachments(merged);
        setPendingFiles([]);
      } catch (err) {
        console.error(err);
        toast.error('Attachment upload failed.');
        throw err;
      } finally {
        setIsUploading(false);
      }
    }

    if (merged.length === 0) return [];

    const withSizes = await ensureAttachmentFileSizes(merged);
    setExistingAttachments(withSizes);
    return withSizes;
  };

  const buildPayload = (
    saveAsDraft: boolean,
    attachments: { name: string; url: string; file_size: number }[],
    conversationOverride?: string | null,
  ) => {
    const deliverables = deliverablesText
      .split('\n')
      .map((d) => d.trim())
      .filter(Boolean)
      .join('\n');

    const formattedMilestones =
      paymentType === 'milestone'
        ? resolvedMilestones().map((m) => ({
            title: m.title.trim(),
            percentage: m.percentage,
            amount: m.amount,
            dueDate: m.dueDate || undefined,
          }))
        : [];

    const hasExpirationDate = Boolean(expirationDate);
    const hasExpirationTime = Boolean(expirationTime);
    let expirationIso: string | null = null;
    if (hasExpirationDate && hasExpirationTime && expirationTimezone) {
      expirationIso = zonedDateTimeIso(expirationDate, expirationTime, expirationTimezone);
    }

    return {
      title: title.trim(),
      description: description.trim() || undefined,
      deliverables: deliverables || undefined,
      currency,
      price: typeof price === 'number' ? price : undefined,
      milestones: formattedMilestones,
      startDate: startDate || null,
      deliveryDate: deliveryDate || null,
      revisionsIncluded: revisionsIncluded || undefined,
      expirationDate: expirationIso,
      timezone: expirationIso ? expirationTimezone : null,
      attachments,
      internalNotes: internalNotes.trim() || undefined,
      cancellationPolicy: cancellationPolicy || null,
      gigUuid: gigUuid || null,
      predecessorOfferUuid: !isEdit ? predecessorOfferUuid : null,
      conversationId: isDirectOffer ? null : (conversationOverride ?? conversationId ?? null),
      recipientUserId: recipientUserId ?? null,
      recipientEmail: recipientUserId == null ? recipientEmail.trim() || null : null,
      saveAsDraft,
    };
  };

  const resolvedMilestones = () =>
    reconcileMilestoneAmounts(
      milestones.filter(
        (m) => m.title.trim() || (m.percentage || 0) > 0 || (m.amount || 0) > 0 || m.dueDate,
      ),
      numericPrice,
    );

  const validate = (mode: 'draft' | 'publish'): boolean => {
    const errors: FieldErrors = {};
    const isPublish = mode === 'publish';

    if (isPublish) {
      if (!title.trim()) errors.title = 'Title is required.';
      if (!description.trim()) errors.description = 'Description is required.';
      if (!deliverablesText.trim()) errors.deliverables = 'Deliverables are required.';
    }

    if (price !== '') {
      if (!Number.isFinite(numericPrice)) {
        errors.price = 'Enter a valid price.';
      } else if (numericPrice < 0) {
        errors.price = 'Price cannot be negative.';
      } else if (isPublish && numericPrice <= 0) {
        errors.price = 'Price must be greater than 0.';
      } else if (isPublish && numericPrice < MIN_OFFER_PAYMENT_AMOUNT) {
        errors.price = MIN_OFFER_AMOUNT_MESSAGE;
      }
    } else if (isPublish) {
      errors.price = 'Enter a valid price.';
    }

    if (isPublish && !cancellationPolicy) {
      errors.cancellationPolicy = 'Cancellation policy is required.';
    }

    if (recipientUserId == null) {
      if (isPublish && !recipientEmail.trim()) {
        errors.recipientEmail = 'Recipient email is required.';
      } else if (recipientEmail.trim() && !EMAIL_RE.test(recipientEmail.trim())) {
        errors.recipientEmail = 'Enter a valid email address.';
      }
    }

    const today = todayIsoDate();
    if (isPublish && !startDate) {
      errors.startDate = 'Start date is required.';
    } else if (isPublish && startDate < today) {
      errors.startDate = 'Start date cannot be in the past.';
    }
    if (isPublish && !deliveryDate) {
      errors.deliveryDate = 'Delivery date is required.';
    }
    if (startDate && deliveryDate && deliveryDate < startDate) {
      errors.deliveryDate = 'Delivery date must be on or after the start date.';
    }

    const hasExpirationDate = Boolean(expirationDate);
    const hasExpirationTime = Boolean(expirationTime);
    const hasExpirationTimezone = Boolean(expirationTimezone);
    const anyExpirationPart = hasExpirationDate || hasExpirationTime;

    if (isPublish && !anyExpirationPart) {
      errors.expirationDate = 'Offer expiration date and time are required.';
    }

    if (anyExpirationPart) {
      if (hasExpirationDate && !hasExpirationTime) {
        errors.expirationTime = 'Please select an expiration time.';
      } else if (hasExpirationTime && !hasExpirationDate) {
        errors.expirationDate = 'Please select an expiration date.';
      } else if (hasExpirationDate && hasExpirationTime && !hasExpirationTimezone) {
        errors.expirationTimezone = 'Please select a time zone.';
      } else if (hasExpirationDate && hasExpirationTime && hasExpirationTimezone) {
        const expirationIso = zonedDateTimeIso(expirationDate, expirationTime, expirationTimezone);
        if (Date.parse(expirationIso) <= Date.now()) {
          errors.expirationDate = 'Offer expiration must be set to a future date and time.';
        }
      }
    }

    if (paymentType === 'milestone' && isPublish) {
      if (milestones.length === 0) {
        errors.milestones = 'Add at least one milestone, or switch to Single payments.';
      } else {
        const unnamed = milestones.some((m) => !m.title.trim());
        const missingDue = milestones.some((m) => !m.dueDate);
        const zeroOrNeg = milestones.some(
          (m) =>
            (!Number.isFinite(m.percentage) || m.percentage <= 0) &&
            (!Number.isFinite(m.amount) || m.amount <= 0),
        );
        const overHundred = milestones.some((m) => m.percentage > 100);
        const dueBeforeStart = startDate
          ? milestones.some((m) => m.dueDate && m.dueDate < startDate)
          : false;
        const dueAfterDelivery = deliveryDate
          ? milestones.some((m) => m.dueDate && m.dueDate > deliveryDate)
          : false;
        const duePast = milestones.some((m) => m.dueDate && m.dueDate < today);
        const resolved = resolvedMilestones();
        const belowMinimum = resolved.some((m) => m.amount < MIN_OFFER_PAYMENT_AMOUNT);
        const resolvedTotal = resolved.reduce((sum, m) => sum + m.amount, 0);
        const totalMismatch = numericPrice > 0 && Math.abs(resolvedTotal - numericPrice) > 0.01;

        if (isPublish && unnamed) {
          errors.milestones = 'Each milestone needs a name.';
        } else if (isPublish && missingDue) {
          errors.milestones = 'Each milestone needs a due date.';
        } else if (isPublish && zeroOrNeg) {
          errors.milestones = 'Each milestone needs a percentage or amount greater than 0.';
        } else if (overHundred) {
          errors.milestones = 'A milestone percentage cannot exceed 100%.';
        } else if (totalPercentage > 100.01) {
          errors.milestones = `Milestone percentages total ${totalPercentage.toFixed(1)}% — that exceeds 100% of the offer price.`;
        } else if (isPublish && Math.abs(totalPercentage - 100) > 0.5) {
          errors.milestones = `Milestone percentages must total 100% (currently ${totalPercentage.toFixed(1)}%).`;
        } else if (totalMismatch) {
          errors.milestones = MILESTONE_TOTAL_MESSAGE;
        } else if (belowMinimum) {
          errors.milestones = MIN_MILESTONE_AMOUNT_MESSAGE;
        } else if (duePast) {
          errors.milestones = 'Milestone due dates cannot be in the past.';
        } else if (dueBeforeStart) {
          errors.milestones = 'Milestone due dates must be on or after the start date.';
        } else if (dueAfterDelivery) {
          errors.milestones = 'Milestone due dates must be on or before the delivery date.';
        }
      }
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      requestAnimationFrame(() => scrollToFirstError(errors));
      return false;
    }
    return true;
  };

  const assertCreatorCanPublish = (): boolean => {
    if (!isDirectOffer) return true;
    if (stripeLoading) {
      toast.error('Still checking your payment setup. Please try again in a moment.');
      return false;
    }
    if (isStripeAccountReady(stripeAccount)) return true;
    setShowStripePrompt(true);
    return false;
  };

  const assertNoActiveDuplicate = async (): Promise<boolean> => {
    const refreshed = await refetchMyOffers();
    if (refreshed.isError || !refreshed.data) {
      toast.error("We couldn't verify your existing offers. Please try again.");
      return false;
    }
    const conflict = findConflictingActiveOffer(refreshed.data.results, {
      gigUuid,
      recipientUserId,
      recipientEmail: recipientUserId == null ? recipientEmail : null,
      excludeOfferId: isEdit ? initialOffer?.id : undefined,
    });
    if (!conflict) return true;
    toast.error(
      isDirectOffer
        ? 'This recipient already has an active direct offer.'
        : 'This candidate already has an active offer for this gig.',
    );
    return false;
  };

  const resolveConversationId = async (): Promise<string | null> => {
    if (conversationId) return conversationId;
    // POST /conversations/ requires recipient_id plus a gig/service context; direct offers
    // (no gig) or email-only recipients rely on the offers endpoint instead.
    if (!gigUuid || recipientUserId == null) return null;
    try {
      const conv = await openConversation.mutateAsync({
        recipient_id: recipientUserId,
        context_type: 'gig',
        context_id: gigUuid,
        ...(applicationUuid ? { gig_application_uuid: applicationUuid } : {}),
        ...(title.trim() ? { title: title.trim() } : {}),
      });
      return conv?.conversation_uuid || null;
    } catch {
      toast.error('Unable to open the candidate chat. Please try again.');
      throw new Error('conversation');
    }
  };

  const finish = (offer: OfferItem, conversationOverride?: string | null) => {
    onSubmitSuccess({
      offerId: offer.id,
      conversationId: isDirectOffer
        ? null
        : offer.conversationId || conversationOverride || conversationId,
      status: offer.status,
    });
  };

  const runExclusive = async (task: () => Promise<void>) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await task();
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleCreate = (saveAsDraft: boolean) =>
    runExclusive(async () => {
      if (!validate(saveAsDraft ? 'draft' : 'publish')) {
        if (!saveAsDraft) {
          toast.error('Please complete all required offer details before sending.');
        }
        return;
      }
      if (!saveAsDraft && !assertCreatorCanPublish()) return;
      if (!saveAsDraft && !(await assertNoActiveDuplicate())) return;
      try {
        const nextConversationId = saveAsDraft ? conversationId : await resolveConversationId();
        const attachments = await resolveAttachments();
        const created = await createOffer.mutateAsync(
          buildCreateOfferPayload(buildPayload(saveAsDraft, attachments, nextConversationId)),
        );
        finish(created, nextConversationId);
      } catch {
        // toast handled upstream
      }
    });

  const handleUpdate = (alsoSubmit: boolean) =>
    runExclusive(async () => {
      if (!initialOffer) return;
      await performUpdate(initialOffer, alsoSubmit);
    });

  const performUpdate = async (original: OfferItem, alsoSubmit: boolean) => {
    const keepAsDraft = original.status === 'draft' && !alsoSubmit;
    if (!validate(keepAsDraft ? 'draft' : 'publish')) {
      if (!keepAsDraft) {
        toast.error('Please complete all required offer details before sending.');
      }
      return;
    }
    if (!keepAsDraft && !assertCreatorCanPublish()) return;
    if (!keepAsDraft && !(await assertNoActiveDuplicate())) return;
    try {
      const nextConversationId = keepAsDraft ? conversationId : await resolveConversationId();
      const attachments = await resolveAttachments();
      const willSubmit =
        alsoSubmit && (original.status === 'draft' || original.status === 'changes_requested');
      const updated = await updateOffer.mutateAsync({
        offerUuid: original.id,
        payload: buildUpdateOfferPayload(
          buildPayload(keepAsDraft, attachments, nextConversationId),
        ),
        silent: willSubmit,
      });
      let sent = updated;
      // The PATCH may already have re-sent the offer; publishing it again would 400.
      if (willSubmit && (updated.status === 'draft' || updated.status === 'changes_requested')) {
        sent = await submitOffer.mutateAsync(original.id);
      } else if (willSubmit) {
        toast.success('Offer sent successfully');
      }
      finish(sent, nextConversationId);
    } catch {
      // toast handled upstream
    }
  };

  return (
    <div className="space-y-6 max-w-[1360px] mx-auto pb-24">
      <div className="flex items-center gap-2 text-xs font-semibold text-zinc-400">
        <button
          onClick={onBack}
          className="flex items-center gap-1 hover:text-white transition cursor-pointer"
        >
          <FiChevronLeft size={16} /> Offers
        </button>
        <span>/</span>
        <span className="text-white font-normal">{isEdit ? 'Edit' : 'Create'}</span>
      </div>

      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold tracking-tight text-white">{formTitle}</h1>
        {/* The form tour lives on /offers/create; starting it from edit would navigate away. */}
        {!isEdit && <TakeTourButton tourId="offer-form" />}
      </div>
      {(!isEdit || initialOffer?.status === 'draft') && (
        <p className="text-xs text-zinc-500 -mt-4">
          Fields marked <span className="text-red-400">*</span> are required to send. You can save a
          draft with partial details and finish later.
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-3xl border border-[#22242a] bg-[#121316] p-4 sm:p-6 space-y-6">
            <h2 className="text-base font-bold text-white">Offer Details</h2>

            <div data-field="title">
              <FormLabel required className="mb-2 text-[11px] font-bold uppercase tracking-wider">
                {isDirectOffer ? 'Offer title' : 'Gig title'}
              </FormLabel>
              <input
                type="text"
                placeholder="e.g. Live Performance at Warehouse 23"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  clearError('title');
                }}
                className={`${fieldClass} ${fieldErrors.title ? fieldErrorClass : ''}`}
              />
              <FieldError message={fieldErrors.title} />
            </div>

            <div data-field="description">
              <FormLabel required className="mb-2 text-[11px] font-bold uppercase tracking-wider">
                Project description
              </FormLabel>
              <textarea
                rows={3}
                placeholder="Describe the project scope, vision, and expectations..."
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  clearError('description');
                }}
                className={`${fieldClass} resize-none ${fieldErrors.description ? fieldErrorClass : ''}`}
              />
              <FieldError message={fieldErrors.description} />
            </div>

            <div data-field="deliverables">
              <FormLabel required className="mb-2 text-[11px] font-bold uppercase tracking-wider">
                Deliverables
              </FormLabel>
              <textarea
                rows={3}
                placeholder="List each deliverable on a new line..."
                value={deliverablesText}
                onChange={(e) => {
                  setDeliverablesText(e.target.value);
                  clearError('deliverables');
                }}
                className={`${fieldClass} resize-none ${fieldErrors.deliverables ? fieldErrorClass : ''}`}
              />
              <FieldError message={fieldErrors.deliverables} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <FormLabel
                  tip={offerFormTips.currency}
                  className="mb-2 text-[11px] font-bold uppercase tracking-wider"
                >
                  Currency
                </FormLabel>
                <input
                  type="text"
                  value={OFFER_CURRENCY_LABEL}
                  readOnly
                  aria-label="Currency"
                  className={`${fieldClass} cursor-not-allowed text-zinc-400`}
                />
              </div>

              <div data-field="price" {...tourTarget(TOUR.offerFormPrice)}>
                <FormLabel
                  required
                  tip={offerFormTips.price}
                  className="mb-2 text-[11px] font-bold uppercase tracking-wider"
                >
                  Price
                </FormLabel>
                <div className="relative">
                  <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-zinc-500">
                    {currencySymbol(currency)}
                  </span>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={price}
                    onChange={(e) => {
                      setPrice(e.target.value ? Number(e.target.value) : '');
                      clearError('price');
                    }}
                    className={`${numberClass} pl-8 ${fieldErrors.price ? fieldErrorClass : ''}`}
                  />
                </div>
                <FieldError message={fieldErrors.price} />
              </div>
            </div>

            <div data-field="cancellationPolicy">
              <FormLabel
                required
                tip={cancellationTermsTip}
                tipLabel="Cancellation policy refund terms"
                tipMaxWidth={320}
                className="mb-2 text-[11px] font-bold uppercase tracking-wider"
              >
                Cancellation
              </FormLabel>
              <Select<CancellationPolicy | ''>
                value={cancellationPolicy}
                options={CANCELLATION_OPTIONS}
                placeholder="Select cancellation"
                aria-label="Cancellation"
                error={!!fieldErrors.cancellationPolicy}
                onChange={(value) => {
                  setCancellationPolicy(value);
                  clearError('cancellationPolicy');
                }}
              />
              <FieldError message={fieldErrors.cancellationPolicy} />
            </div>

            <div
              data-field="milestones"
              {...tourTarget(TOUR.offerFormSchedule)}
              className="rounded-2xl border border-[#22242a] bg-[#0c0d0f] p-5 space-y-5"
            >
              <span className="text-xs font-bold text-white uppercase tracking-wider">
                Payment schedule
              </span>

              <div>
                <FormLabel
                  tip={offerFormTips.paymentType}
                  className="mb-1.5 text-[10px] font-bold uppercase tracking-wider"
                >
                  Payment type
                </FormLabel>
                <Select
                  value={paymentType}
                  options={PAYMENT_TYPE_OPTIONS}
                  aria-label="Payment type"
                  onChange={(value) => {
                    setPaymentType(value as OfferPaymentType);
                    clearError('milestones');
                  }}
                />
                <p className="mt-1.5 text-[11px] text-zinc-500">
                  {paymentType === 'single'
                    ? 'Client pays the full amount upon completion.'
                    : 'Split the price across milestones totalling 100%. Enter a percentage or amount for each — they stay in sync. Name and due date are required.'}
                </p>
              </div>

              {paymentType === 'milestone' && (
                <div className="space-y-4 pt-2 border-t border-[#22242a]">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-zinc-300 uppercase tracking-wider">
                      Milestones
                      <InfoTip content={offerFormTips.milestones} label="About milestones" />
                    </span>
                    <button
                      type="button"
                      onClick={handleAddMilestone}
                      className="inline-flex items-center gap-1 text-xs font-bold text-blue-400 hover:text-blue-300 transition cursor-pointer"
                    >
                      <FiPlus size={14} /> Add milestone
                    </button>
                  </div>

                  <div className="space-y-3">
                    {milestones.map((m) => {
                      const highlightPct =
                        !!fieldErrors.milestones &&
                        ((m.percentage <= 0 && m.amount <= 0) ||
                          totalPercentage > 100 ||
                          Math.abs(totalPercentage - 100) > 0.5);
                      return (
                        <div key={m.id} className="space-y-1.5">
                          <div className="flex items-start gap-2">
                            <div className="min-w-0 flex-1 space-y-3">
                              <div>
                                <label className="block text-[9px] font-bold text-zinc-500 uppercase tracking-wider mb-1">
                                  Milestone name <span className="text-red-400">*</span>
                                </label>
                                <input
                                  type="text"
                                  placeholder="Milestone name"
                                  value={m.title}
                                  onChange={(e) =>
                                    handleMilestoneChange(m.id, 'title', e.target.value)
                                  }
                                  className={`${nestedFieldClass} ${
                                    fieldErrors.milestones && !m.title.trim() ? fieldErrorClass : ''
                                  }`}
                                />
                              </div>

                              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                                <div>
                                  <label className="block text-[9px] font-bold text-zinc-500 uppercase tracking-wider mb-1">
                                    Percentage <span className="text-red-400">*</span>
                                  </label>
                                  <div className="relative">
                                    <input
                                      type="number"
                                      placeholder="0"
                                      min={0}
                                      max={100}
                                      step={0.1}
                                      value={m.percentage || ''}
                                      onChange={(e) =>
                                        handleMilestoneChange(
                                          m.id,
                                          'percentage',
                                          e.target.value === '' ? 0 : Number(e.target.value),
                                        )
                                      }
                                      className={`${nestedNumberClass} pr-7 ${
                                        highlightPct || totalPercentage > 100 ? fieldErrorClass : ''
                                      }`}
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500">
                                      %
                                    </span>
                                  </div>
                                </div>

                                <div>
                                  <label className="block text-[9px] font-bold text-zinc-500 uppercase tracking-wider mb-1">
                                    Amount <span className="text-red-400">*</span>
                                  </label>
                                  <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500">
                                      {currencySymbol(currency)}
                                    </span>
                                    <input
                                      type="number"
                                      placeholder="0.00"
                                      min={0}
                                      max={numericPrice > 0 ? numericPrice : undefined}
                                      step={0.01}
                                      value={m.amount || ''}
                                      onChange={(e) =>
                                        handleMilestoneChange(
                                          m.id,
                                          'amount',
                                          e.target.value === '' ? 0 : Number(e.target.value),
                                        )
                                      }
                                      className={`${nestedNumberClass} pl-7 ${
                                        highlightPct || totalPercentage > 100 ? fieldErrorClass : ''
                                      }`}
                                    />
                                  </div>
                                </div>

                                <div className="col-span-2 sm:col-span-1">
                                  <label className="block text-[9px] font-bold text-zinc-500 uppercase tracking-wider mb-1">
                                    Due date <span className="text-red-400">*</span>
                                  </label>
                                  <input
                                    type="date"
                                    value={m.dueDate}
                                    min={startDate || undefined}
                                    max={deliveryDate || undefined}
                                    onChange={(e) =>
                                      handleMilestoneChange(m.id, 'dueDate', e.target.value)
                                    }
                                    className={`${nestedDateClass} ${
                                      fieldErrors.milestones && !m.dueDate ? fieldErrorClass : ''
                                    }`}
                                  />
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveMilestone(m.id)}
                              className="mt-6 text-zinc-500 hover:text-red-400 transition cursor-pointer flex-shrink-0 p-1"
                              aria-label="Remove milestone"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <FieldError message={fieldErrors.milestones} />

                  {milestones.length > 0 && (
                    <div
                      className={`rounded-xl border p-4 space-y-2 text-xs ${
                        totalPercentage > 100
                          ? 'border-red-500/40 bg-red-500/5'
                          : Math.abs(totalPercentage - 100) > 0.5
                            ? 'border-amber-500/30 bg-amber-500/5'
                            : 'border-[#22242a] bg-[#121316]'
                      }`}
                    >
                      <span className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1">
                        Total breakdown
                      </span>
                      {milestones.map((m) => (
                        <div key={m.id} className="flex justify-between text-zinc-400">
                          <span>{m.title || 'Untitled'}</span>
                          <span>
                            {currencySymbol(currency)}
                            {(m.amount || 0).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{' '}
                            ({m.percentage || 0}%)
                          </span>
                        </div>
                      ))}
                      <div className="flex justify-between font-bold text-white pt-2 border-t border-[#22242a]">
                        <span>Total</span>
                        <span
                          className={
                            totalPercentage > 100
                              ? 'text-red-400'
                              : Math.abs(totalPercentage - 100) <= 0.5
                                ? 'text-blue-400'
                                : 'text-amber-400'
                          }
                        >
                          {currencySymbol(currency)}
                          {totalMilestoneAmount.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}{' '}
                          · {totalPercentage}%
                          {totalPercentage > 100
                            ? ' · over budget'
                            : Math.abs(totalPercentage - 100) > 0.5
                              ? ` · ${totalPercentage < 100 ? 'short' : 'over'} by ${Math.abs(100 - totalPercentage).toFixed(1)}%`
                              : ''}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div data-field="startDate">
                <FormLabel required className="mb-2 text-[11px] font-bold uppercase tracking-wider">
                  Start date
                </FormLabel>
                <input
                  type="date"
                  value={startDate}
                  min={todayIsoDate()}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    clearError('startDate');
                    clearError('deliveryDate');
                    clearError('milestones');
                  }}
                  className={`${dateClass} ${fieldErrors.startDate ? fieldErrorClass : ''}`}
                />
                <FieldError message={fieldErrors.startDate} />
              </div>

              <div data-field="deliveryDate">
                <FormLabel required className="mb-2 text-[11px] font-bold uppercase tracking-wider">
                  Delivery date
                </FormLabel>
                <input
                  type="date"
                  value={deliveryDate}
                  min={startDate || undefined}
                  onChange={(e) => {
                    setDeliveryDate(e.target.value);
                    clearError('deliveryDate');
                    clearError('milestones');
                  }}
                  className={`${dateClass} ${fieldErrors.deliveryDate ? fieldErrorClass : ''}`}
                />
                <FieldError message={fieldErrors.deliveryDate} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <FormLabel
                  required
                  tip={offerFormTips.revisions}
                  className="mb-2 text-[11px] font-bold uppercase tracking-wider"
                >
                  Revisions included
                </FormLabel>
                <Select
                  value={revisionsIncluded}
                  options={REVISION_OPTIONS}
                  aria-label="Revisions included"
                  onChange={setRevisionsIncluded}
                />
              </div>
            </div>

            <div
              data-field="expirationDate"
              className="space-y-4"
              {...tourTarget(TOUR.offerFormExpiration)}
            >
              <FormLabel
                required
                tip={offerFormTips.expiration}
                className="mb-0 text-[11px] font-bold uppercase tracking-wider"
              >
                Offer expiration
              </FormLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <FormLabel required className="mb-2 text-[11px] font-semibold text-zinc-400">
                    Expiration date
                  </FormLabel>
                  <input
                    type="date"
                    value={expirationDate}
                    min={todayInZone(expirationTimezone)}
                    onChange={(e) => {
                      setExpirationDate(e.target.value);
                      clearError('expirationDate');
                      clearError('expirationTime');
                      clearError('expirationTimezone');
                    }}
                    className={`${dateClass} ${fieldErrors.expirationDate ? fieldErrorClass : ''}`}
                  />
                  <FieldError message={fieldErrors.expirationDate} />
                </div>
                <div data-field="expirationTime">
                  <FormLabel required className="mb-2 text-[11px] font-semibold text-zinc-400">
                    Expiration time
                  </FormLabel>
                  <input
                    type="time"
                    value={expirationTime}
                    onChange={(e) => {
                      setExpirationTime(e.target.value);
                      clearError('expirationDate');
                      clearError('expirationTime');
                      clearError('expirationTimezone');
                    }}
                    className={`${dateClass} ${fieldErrors.expirationTime ? fieldErrorClass : ''}`}
                  />
                  <FieldError message={fieldErrors.expirationTime} />
                </div>
              </div>
              <div data-field="expirationTimezone">
                <FormLabel
                  required
                  tip={offerFormTips.timezone}
                  className="mb-2 text-[11px] font-semibold text-zinc-400"
                >
                  Expiration time zone
                </FormLabel>
                <TimezoneSelect
                  value={expirationTimezone}
                  onChange={(value) => {
                    setExpirationTimezone(value);
                    clearError('expirationDate');
                    clearError('expirationTime');
                    clearError('expirationTimezone');
                  }}
                />
                <FieldError message={fieldErrors.expirationTimezone} />
              </div>
              {expirationDate && expirationTime && expirationTimezone && (
                <p className="text-[12px] text-zinc-400">
                  Expires{' '}
                  <span className="text-zinc-200 font-medium">
                    {formatDateTimeLong(
                      zonedDateTimeIso(expirationDate, expirationTime, expirationTimezone),
                      expirationTimezone,
                    )}
                  </span>
                </p>
              )}
              {expiryAlreadyPast && !fieldErrors.expirationDate && (
                <p className="rounded-xl border border-amber-500/20 bg-amber-500/[0.07] px-3 py-2 text-[12px] text-amber-200">
                  This expiration has already passed. Pick a new date and time before sending.
                </p>
              )}
              <p className="text-[11px] text-zinc-500">Must be a future date and time.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <FormLabel optional className="mb-2 text-[11px] font-bold uppercase tracking-wider">
                  Attachments
                </FormLabel>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={OFFER_ATTACHMENT_ACCEPT_ATTRIBUTE}
                  className="hidden"
                  disabled={attachmentsAtLimit}
                  onChange={(e) => handleFilesSelected(e.target.files)}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (attachmentsAtLimit) {
                      toast.error(`You can attach up to ${UPLOAD_MAX_MEDIA_FILES} files.`);
                      return;
                    }
                    fileInputRef.current?.click();
                  }}
                  disabled={attachmentsAtLimit}
                  className={`w-full rounded-2xl border border-dashed border-white/15 bg-[#0c0d0f] p-6 text-center focus:outline-none focus:ring-1 focus:ring-blue-500/40 transition ${
                    attachmentsAtLimit
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:border-white/25 cursor-pointer'
                  }`}
                >
                  <FiUploadCloud size={24} className="mx-auto text-zinc-500 mb-2" />
                  <p className="text-xs text-zinc-400">
                    Drop files here or <span className="text-blue-400 font-semibold">browse</span>
                  </p>
                  <p className="text-[10px] text-zinc-600 mt-1">
                    PDF, ZIP, DOC, PNG, JPG — up to {UPLOAD_MAX_MEDIA_FILES} files, 10 MB each
                    {attachmentCount > 0 ? ` (${attachmentCount}/${UPLOAD_MAX_MEDIA_FILES})` : ''}
                  </p>
                </button>

                {(existingAttachments.length > 0 || pendingFiles.length > 0) && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {existingAttachments.map((att) => (
                      <span
                        key={att.url}
                        className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-[#0c0d0f] px-3 py-1.5 text-[11px] text-zinc-300"
                      >
                        <FiPaperclip size={12} />
                        <span className="max-w-[140px] truncate">{att.name}</span>
                        <button
                          type="button"
                          onClick={() =>
                            setExistingAttachments((prev) => prev.filter((a) => a.url !== att.url))
                          }
                          className="text-zinc-500 hover:text-red-400"
                        >
                          <FiX size={12} />
                        </button>
                      </span>
                    ))}
                    {pendingFiles.map((file, idx) => (
                      <span
                        key={`${file.name}-${idx}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-blue-500/30 bg-blue-500/10 px-3 py-1.5 text-[11px] text-blue-300"
                      >
                        <FiPaperclip size={12} />
                        <span className="max-w-[140px] truncate">{file.name}</span>
                        <button
                          type="button"
                          onClick={() =>
                            setPendingFiles((prev) => prev.filter((_, i) => i !== idx))
                          }
                          className="text-blue-400/70 hover:text-red-400"
                        >
                          <FiX size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <FormLabel
                  optional
                  tip={offerFormTips.internalNotes}
                  className="mb-2 text-[11px] font-bold uppercase tracking-wider"
                >
                  Internal notes
                </FormLabel>
                <textarea
                  rows={4}
                  placeholder="Notes visible only to you..."
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  className={`${fieldClass} resize-none`}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div
            className="rounded-3xl border border-[#22242a] bg-[#121316] p-4 sm:p-6 space-y-4"
            {...tourTarget(TOUR.offerFormFees)}
          >
            <h3 className="text-base font-bold text-white">
              {isDirectOffer ? 'Payout Summary' : 'Payment Summary'}
            </h3>
            <OfferFeeSummary
              currency={currency}
              gigAmount={numericPrice}
              isDirectOffer={isDirectOffer}
              perspective={isDirectOffer ? 'payee' : 'payer'}
              viewerRole={isDirectOffer ? 'payee' : 'payer'}
            />
          </div>

          <div className="rounded-3xl border border-[#22242a] bg-[#121316] p-4 sm:p-6 space-y-4">
            <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Sending To</h3>
            <div data-field="recipientEmail" {...tourTarget(TOUR.offerFormRecipient)}>
              {(recipientLocked || recipientName.trim() || recipientEmail.trim()) && (
                <div className="mb-4 flex items-center gap-3 rounded-2xl border border-[#22242a] bg-[#0c0d0f] p-3.5">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-violet-600 text-sm font-bold text-white flex-shrink-0">
                    {(recipientName || recipientEmail || 'R').trim().charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-white truncate">
                      {recipientName.trim() || recipientEmail.trim() || 'Applicant'}
                    </p>
                    <p className="text-[11px] text-zinc-500 truncate mt-0.5">
                      {recipientName.trim() && recipientEmail.trim()
                        ? recipientEmail
                        : initialOffer?.recipientRole ||
                          (recipientUserId != null ? `User #${recipientUserId}` : 'Recipient')}
                    </p>
                  </div>
                  {!recipientLocked && (
                    <Tooltip content="Remove recipient">
                      <button
                        type="button"
                        onClick={() => {
                          setRecipientEmail('');
                          setRecipientName('');
                          setRecipientUserId(null);
                          clearError('recipientEmail');
                        }}
                        className="p-2 rounded-lg text-red-400/80 hover:text-red-400 hover:bg-red-500/10 transition cursor-pointer"
                        aria-label="Remove recipient"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </Tooltip>
                  )}
                </div>
              )}

              {recipientLocked ? (
                <p className="text-[10px] text-zinc-500">
                  This offer is tied to the applicant you accepted.
                </p>
              ) : (
                <>
                  <FormLabel
                    required={recipientUserId == null}
                    optional={recipientUserId != null}
                    tip={offerFormTips.recipient}
                    className="mb-1.5 text-[10px] font-bold uppercase tracking-wider"
                  >
                    Recipient email
                  </FormLabel>
                  <input
                    type="email"
                    placeholder="artist@example.com"
                    value={recipientEmail}
                    onChange={(e) => {
                      setRecipientEmail(e.target.value);
                      clearError('recipientEmail');
                    }}
                    className={`${fieldClass} ${fieldErrors.recipientEmail ? fieldErrorClass : ''}`}
                  />
                  <FieldError message={fieldErrors.recipientEmail} />
                </>
              )}
            </div>
            <div>
              <FormLabel optional className="mb-1.5 text-[10px] font-bold uppercase tracking-wider">
                Display name
              </FormLabel>
              <input
                type="text"
                placeholder="Name shown in your notes"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                className={fieldClass}
              />
            </div>
            {!isDirectOffer && conversationId && (
              <p className="text-[10px] text-zinc-500">Conversation attached</p>
            )}
          </div>

          <div className="space-y-3">
            {isEdit ? (
              <>
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() => handleUpdate(true)}
                  className="w-full rounded-full bg-white py-3.5 px-6 text-sm font-bold text-black transition hover:bg-zinc-200 cursor-pointer shadow-lg disabled:opacity-60"
                >
                  {isUploading ? 'Uploading…' : isSaving ? 'Sending…' : sendLabel}
                </button>
                {initialOffer?.status === 'draft' && (
                  <button
                    type="button"
                    disabled={isSaving}
                    onClick={() => handleUpdate(false)}
                    className="w-full rounded-full border border-zinc-700 bg-transparent py-3.5 px-6 text-sm font-bold text-white transition hover:bg-zinc-900 cursor-pointer disabled:opacity-60"
                  >
                    {isUploading ? 'Uploading…' : isSaving ? 'Saving…' : 'Save Draft'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={onBack}
                  className="w-full rounded-full border border-zinc-700 bg-transparent py-3.5 px-6 text-sm font-bold text-white transition hover:bg-zinc-900 cursor-pointer"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() => handleCreate(false)}
                  className="w-full rounded-full bg-white py-3.5 px-6 text-sm font-bold text-black transition hover:bg-zinc-200 cursor-pointer shadow-lg disabled:opacity-60"
                >
                  {isUploading ? 'Uploading…' : isSaving ? 'Sending…' : sendLabel}
                </button>
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() => handleCreate(true)}
                  className="w-full rounded-full border border-zinc-700 bg-transparent py-3.5 px-6 text-sm font-bold text-white transition hover:bg-zinc-900 cursor-pointer disabled:opacity-60"
                >
                  {isUploading ? 'Uploading…' : isSaving ? 'Saving…' : 'Save Draft'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <StripeOnboardingPromptModal
        isOpen={showStripePrompt}
        onClose={() => setShowStripePrompt(false)}
      />
    </div>
  );
};
